// This may be a local issue but some assignments do not contain a driver
// or a vehicle. I know this has happened in production before, so I want to be safe.
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import { createColumnHelper } from '@tanstack/react-table';
import { truncate } from 'lodash-es';
import { DateTime } from 'luxon';

import store from 'src/app/store/store';
import { DriverColumnFilter } from 'src/common/datagridColumnFilters/DriverColumnFilter';
import { VehicleColumnFilter } from 'src/common/datagridColumnFilters/VehicleColumnFilter';
import { AssignmentRead } from 'src/common/external/bambi-api/bambiApi';
import {
  setDataGridSelectedRange,
  setFilterDrivers,
  setFilterVehicles,
} from 'src/features/team/assignment-management/assignmentManagement.slice';
import { DateColumnFilter } from 'src/features/trip/management/filters/DateColumnFilter';

import { RowActions } from './RowActions';
import { SelectAllHeader } from './SelectAllHeader';

const columnHelper = createColumnHelper<AssignmentRead>();

export const columns = [
  columnHelper.display({
    id: 'selection',
    header: ({ table }) => <SelectAllHeader table={table} />,
    enableHiding: false,
    meta: {
      enableHeaderMenu: false,
    },
    cell: ({ row }) => {
      return (
        <input
          data-testid={`row-selection-control-${row.original.id}`}
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      );
    },
  }),
  columnHelper.accessor('driver', {
    header: 'Driver',
    cell: ({ row }) =>
      `${row.original.driver?.first_name} ${row.original.driver?.last_name}`,
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DriverColumnFilter
            open={open}
            onClose={onClose}
            selected={store.getState().assignmentManagement.filterDrivers}
            setSelectedAction={setFilterDrivers}
          />
        );
      },
      filterCount() {
        return store.getState().assignmentManagement.filterDrivers.length;
      },
    },
  }),
  columnHelper.accessor('attendants', {
    header: 'Attendants',
    cell: ({ row }) =>
      truncate(
        row.original.attendants
          .map(
            (attendant) => `${attendant?.first_name} ${attendant?.last_name}`
          )
          .join(', '),
        { length: 75 }
      ),
  }),
  columnHelper.accessor('vehicle', {
    header: 'Vehicle',
    cell: ({ row }) => row.original.vehicle?.nickname,
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <VehicleColumnFilter
            open={open}
            onClose={onClose}
            selectedVehicles={
              store.getState().assignmentManagement.filterVehicles
            }
            onChange={(values) => store.dispatch(setFilterVehicles(values))}
          />
        );
      },
      filterCount() {
        return store.getState().assignmentManagement.filterVehicles.length;
      },
    },
  }),

  columnHelper.accessor('start', {
    header: 'Scheduled start',
    cell: ({ row }) =>
      DateTime.fromISO(row.original.start).toLocaleString(
        DateTime.DATETIME_SHORT
      ),
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DateColumnFilter
            open={open}
            onClose={onClose}
            setSelectedAction={setDataGridSelectedRange}
          />
        );
      },
      filterCount() {
        return store.getState().assignmentManagement.dataGridSelectedRange
          ? 1
          : 0;
      },
    },
  }),
  columnHelper.accessor('end', {
    header: 'Scheduled end',
    cell: ({ row }) =>
      DateTime.fromISO(row.original.end).toLocaleString(
        DateTime.DATETIME_SHORT
      ),
  }),
  columnHelper.display({
    id: 'clock_in_time',
    header: 'Clock in time',
    cell: ({ row }) =>
      row.original.driver?.clocked_in_at
        ? DateTime.fromISO(row.original.driver?.clocked_in_at).toLocaleString(
            DateTime.DATETIME_SHORT
          )
        : '',
  }),
  columnHelper.display({
    id: 'clock_out_time',
    header: 'Clock out time',
    cell: ({ row }) =>
      row.original.driver?.clocked_out_at
        ? DateTime.fromISO(row.original.driver?.clocked_out_at).toLocaleString(
            DateTime.DATETIME_SHORT
          )
        : '',
  }),
  columnHelper.display({
    id: 'manage',
    header: 'Manage',
    cell: ({ row }) => <RowActions assignment={row.original} />,
  }),
];
